import { Project, SnaphotReference, Snapshot } from '@/models'
import { defineStore } from 'pinia'
import CONSTANTS from '@/constants/Config.constants'
import { useBaseStore } from '.'
import moment, { Moment } from 'moment'
import { formatDateNoTime } from '@/utils/helpers'
import { computed, ref } from 'vue'
import * as HELPERS from '@/utils/helpers'
import notifMessage from '@/translations/notifMessage.json'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export const useSnapshotStore = defineStore('snapshotStore', () => {
  const snapshotService = injectTyped(ServiceKeys.snapshot)
  const uploadService = injectTyped(ServiceKeys.upload)
  const snapshots = ref<Snapshot[]>([])
  const pagination = ref({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
  })
  const status = ref({
    loading: false,
    uploading: false,
  })

  const currentSnapshot = ref<Snapshot | undefined>(undefined)
  const sortCriteria = ref<string>('')
  const filterDate = ref<string>('')
  const filterDateRange = ref<Moment[]>([])
  const filterString = ref<string>('')

  const getSortedAndFiltered = computed((): Snapshot[] => {
    let filteredAndSorted = snapshots.value

    if (filterDateRange.value.length > 0 && filterDateRange.value[0] && filterDateRange.value[1]) {
      filteredAndSorted = filterByDateRange(filteredAndSorted, filterDateRange.value as [])
    }

    if (sortCriteria.value === CONSTANTS.SORTING_OPTIONS[0]) {
      filteredAndSorted = filterByName(filteredAndSorted, filterString.value)
      filteredAndSorted.sort((a, b) => a.name.localeCompare(b.name))
    }
    return filteredAndSorted
  })

  const sortedSnapshots = computed((): Snapshot[] => {
    const sortedSnapshots = snapshots.value

    if (sortCriteria.value === CONSTANTS.SORTING_OPTIONS[0]) {
      sortedSnapshots.sort((a, b) => a.name.localeCompare(b.name))
    }

    return sortedSnapshots
  })

  async function loadSnapshotById(projectId: number, snapshotId: SnaphotReference) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const snapshot = await snapshotService.getSnapshot(projectId, snapshotId)
      setCurrentSnapshot(snapshot.data)
    } catch (error: any) {
      if (error?.message === 'Snapshot not found') {
        resetSnapshots()
      } else {
        console.error('Error loading snapshot:', error)
      }
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  async function loadSnapshotsFromApi({
    projectId,
    page,
    itemsPerPage,
  }: {
    projectId: number
    page?: number
    itemsPerPage?: number
  }) {
    status.value.loading = true
    const baseStore = useBaseStore()
    baseStore.startNewJob()
    resetSnapshots()

    try {
      const response = await snapshotService.getSnapshots(projectId, page, itemsPerPage)
      setSnapshots(response.data)
      pagination.value.totalItems = response.pagination?.totalElements || 0
      pagination.value.currentPage = response.pagination?.page || 1 // pages are 1-indexed

      if (response.data.length > 0) {
        setCurrentSnapshot(response.data[0])
      }
    } catch (error) {
      console.error('Error loading snapshots:', error)
    } finally {
      baseStore.decrementRunningJobs()
      status.value.loading = false
    }
  }

  async function uploadSnapshot(file: File, project: Project): Promise<Snapshot | undefined> {
    status.value.uploading = true
    try {
      const presignedUrlObj = await uploadService.getPresignedUrl(project.id)
      await uploadService.uploadFileToAWSS3(presignedUrlObj.data.url, file)

      const notifyBackendResponse = await uploadService.notifyUploadSnapshotFinished(
        file.name as string,
        project.id,
        presignedUrlObj.data.id,
      )
      if (notifyBackendResponse) {
        HELPERS.showTopAlertMessage(true, 'success', notifMessage.SUCCESS_ALERTS.SNAPSHOT_UPLOAD)
        addSnapshot(notifyBackendResponse.data)
      }
      return notifyBackendResponse.data
    } catch {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
    } finally {
      status.value.uploading = false
    }
  }

  async function deleteSnapshot({
    projectId,
    snapshot,
  }: {
    projectId: number
    snapshot: Snapshot
  }) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const deleted = await snapshotService.deleteSnapshot(projectId, snapshot)
      removeSnapshot(deleted.data.id)
    } catch (error) {
      console.error('Error deleting snapshot:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  function setSnapshots(newSnapshots: Snapshot[]) {
    snapshots.value = newSnapshots
  }

  function setCurrentSnapshot(snapshot: Snapshot | undefined) {
    currentSnapshot.value = snapshot
  }

  function setSortCriteria(criteria: string) {
    sortCriteria.value = criteria
  }

  function setFilterDate(date: string) {
    filterDate.value = date
  }

  function setFilterDateRange(dateRange: Moment[]) {
    filterDateRange.value = dateRange
  }

  function setFilterString(filter: string) {
    filterString.value = filter
  }

  function resetSnapshots() {
    snapshots.value = []
    currentSnapshot.value = undefined
  }
  function addSnapshot(s: Snapshot) {
    snapshots.value.push(s)
    pagination.value.totalItems += 1
  }
  function removeSnapshot(id: number) {
    snapshots.value.splice(
      snapshots.value.findIndex((s) => s.id === id),
      1,
    )
    pagination.value.totalItems -= 1
  }

  return {
    snapshots,
    currentSnapshot,
    sortCriteria,
    filterDate,
    filterDateRange,
    filterString,
    pagination,
    getSortedAndFiltered,
    sortedSnapshots,
    status,
    loadSnapshotById,
    loadSnapshotsFromApi,
    uploadSnapshot,
    deleteSnapshot,
    setSnapshots,
    setCurrentSnapshot,
    setSortCriteria,
    setFilterDate,
    setFilterDateRange,
    setFilterString,
    resetSnapshots,
  }
})

// Helper functions
function filterByDateRange(list: Snapshot[], date: Moment[]): Array<Snapshot> {
  if (date && date[0] && date[1]) {
    return list.filter((item) => {
      const itemDate = moment(new Date(item.date))
      const [filterRangeBegin, filterRangeEnd] = date

      return itemDate.isBetween(filterRangeBegin, filterRangeEnd, 'day', '[]')
    })
  }
  return list
}

function filterByName(list: Snapshot[], filter: string): Array<Snapshot> {
  if (filter) {
    return list.filter((item) => {
      const itemDateName = `${formatDateNoTime(new Date(item.date).toISOString())} / ${item.name}`
      return itemDateName.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
    })
  }
  return list
}
